import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User } from 'lucide-react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import ExportReportGenerator from '../../components/ui/ExportReportGenerator';
import MetricsCard from './components/MetricsCard';
import ActivityFeed from './components/ActivityFeed';
import SurveyTrendsChart from './components/SurveyTrendsChart';
import PropertySearchBar from './components/PropertySearchBar';
import UrgentTasksPanel from './components/UrgentTasksPanel';
import { API_ENDPOINTS } from '../../utils/config';

const PREVIEW_COUNT = 5;

const TeamPerformanceCard = ({ surveyors }) => {
  const [showAll, setShowAll] = useState(false);
  const colors = ['bg-primary/10 text-primary', 'bg-success/10 text-success', 'bg-accent/10 text-accent'];
  const hasMore = surveyors.length > PREVIEW_COUNT;
  const visible = showAll ? surveyors : surveyors.slice(0, PREVIEW_COUNT);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Team Performance</h3>
        {hasMore && (
          <button
            onClick={() => setShowAll(prev => !prev)}
            className="text-xs font-medium text-primary hover:text-primary/70 transition-colors"
          >
            {showAll ? 'Show Less' : `View All (${surveyors.length})`}
          </button>
        )}
      </div>
      <div className="space-y-4">
        {visible.map((s, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colors[index % colors.length].split(' ')[0]}`}>
                <User className={`w-4 h-4 ${colors[index % colors.length].split(' ')[1]}`} />
              </div>
              <span className="text-sm text-foreground">{s.name}</span>
            </div>
            <span className="text-sm font-medium text-success">{s.surveys} surveys</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [metricsData, setMetricsData] = useState([
    {
      title: "Total Properties",
      value: "0",
      change: "+0%",
      changeType: "neutral",
      icon: "Building",
      color: "var(--color-primary)"
    },
    {
      title: "Total Clients",
      value: "0",
      change: "+0%",
      changeType: "neutral",
      icon: "Users",
      color: "var(--color-success)"
    },
    {
      title: "Active Users",
      value: "0",
      change: "+0%",
      changeType: "neutral",
      icon: "UserCheck",
      color: "var(--color-warning)"
    },
    {
      title: "Total Roles",
      value: "0",
      change: "+0%",
      changeType: "neutral",
      icon: "Shield",
      color: "var(--color-accent)"
    }
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // Fetch all data in parallel
      const [propertiesRes, clientsRes, rolesRes, userRolesRes, analyticsRes] = await Promise.all([
        axios.get(API_ENDPOINTS.PROPERTY_LIST, { headers }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.ROLES_LIST, { headers }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.USER_ROLES_LIST, { headers }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.ANALYTICS_DASHBOARD, { headers }).catch(() => ({ data: null }))
      ]);

      if (analyticsRes.data) setAnalyticsData(analyticsRes.data);

      // Process properties data
      const propertiesData = Array.isArray(propertiesRes.data) 
        ? propertiesRes.data 
        : (propertiesRes.data?.results || propertiesRes.data?.data || propertiesRes.data?.properties || []);

      // Process clients data
      const clientsData = Array.isArray(clientsRes.data) 
        ? clientsRes.data 
        : (clientsRes.data?.results || clientsRes.data?.data || clientsRes.data?.clients || []);

      // Process roles data
      const rolesData = Array.isArray(rolesRes.data) 
        ? rolesRes.data 
        : (rolesRes.data?.results || rolesRes.data?.data || rolesRes.data?.roles || []);

      // Process user roles data
      const userRolesData = Array.isArray(userRolesRes.data)
        ? userRolesRes.data
        : (userRolesRes.data?.results || userRolesRes.data?.data || userRolesRes.data?.user_roles || []);

      // Calculate metrics
      const activeUsers = userRolesData.filter(u => u?.user_detail?.is_active !== false).length;
      
      // Calculate changes (comparing with previous month)
      const currentMonth = new Date().getMonth();
      const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      
      const propertiesThisMonth = propertiesData.filter(p => {
        const createdDate = new Date(p.created_at || p.createdAt);
        return createdDate.getMonth() === currentMonth;
      }).length;

      const clientsThisMonth = clientsData.filter(c => {
        const createdDate = new Date(c.created_at || c.createdAt);
        return createdDate.getMonth() === currentMonth;
      }).length;

      const propertiesLastMonth = propertiesData.filter(p => {
        const createdDate = new Date(p.created_at || p.createdAt);
        return createdDate.getMonth() === previousMonth;
      }).length;

      const clientsLastMonth = clientsData.filter(c => {
        const createdDate = new Date(c.created_at || c.createdAt);
        return createdDate.getMonth() === previousMonth;
      }).length;

      // Calculate percentage changes
      const propertiesChange = propertiesLastMonth > 0 
        ? ((propertiesThisMonth - propertiesLastMonth) / propertiesLastMonth * 100).toFixed(1)
        : propertiesThisMonth > 0 ? 100 : 0;

      const clientsChange = clientsLastMonth > 0 
        ? ((clientsThisMonth - clientsLastMonth) / clientsLastMonth * 100).toFixed(1)
        : clientsThisMonth > 0 ? 100 : 0;

      // Update metrics
      setMetricsData([
        {
          title: "Total Properties",
          value: propertiesData.length.toString(),
          change: `${propertiesChange > 0 ? '+' : ''}${propertiesChange}%`,
          changeType: propertiesChange > 0 ? "positive" : propertiesChange < 0 ? "negative" : "neutral",
          icon: "Building",
          color: "var(--color-primary)"
        },
        {
          title: "Total Clients",
          value: clientsData.length.toString(),
          change: `${clientsChange > 0 ? '+' : ''}${clientsChange}%`,
          changeType: clientsChange > 0 ? "positive" : clientsChange < 0 ? "negative" : "neutral",
          icon: "Users",
          color: "var(--color-success)"
        },
        {
          title: "Active Users",
          value: activeUsers.toString(),
          change: "+0%",
          changeType: "neutral",
          icon: "UserCheck",
          color: "var(--color-warning)"
        },
        {
          title: "Total Roles",
          value: rolesData.length.toString(),
          change: "+0%",
          changeType: "neutral",
          icon: "Shield",
          color: "var(--color-accent)"
        }
      ]);

      setLastUpdated(new Date());
    } catch (error) {
      // Failed to fetch dashboard data handled silently
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
                <p className="text-muted-foreground mt-1">
                  Welcome back! Here's what's happening with your property surveys today.
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-muted-foreground">
                  Last updated: {lastUpdated?.toLocaleTimeString()}
                </span>
                {isLoading && (
                  <span className="text-sm text-muted-foreground animate-pulse">
                    Loading...
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <PropertySearchBar />
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metricsData?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                color={metric?.color}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
            {/* Activity Feed - Left Column */}
            <div className="lg:col-span-8">
              <ActivityFeed />
            </div>

            {/* Urgent Tasks Panel - Right Column */}
            <div className="lg:col-span-4">
              <UrgentTasksPanel />
            </div>
          </div>

          {/* Charts and Analytics */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
            <SurveyTrendsChart
              data={analyticsData?.survey_volume || []}
              completionRate={analyticsData?.metrics?.survey_completion_rate ?? 0}
            />
            <div className="space-y-6">
              <QuickActionToolbar />
              <ExportReportGenerator />
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Survey Types */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Survey Types</h3>
              {analyticsData?.property_distribution?.length > 0 ? (
                <div className="space-y-3">
                  {analyticsData.property_distribution.map((item, index) => {
                    const colors = ['bg-primary', 'bg-success', 'bg-warning', 'bg-accent'];
                    return (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-muted-foreground">{item.type}</span>
                          <span className="text-sm font-medium text-foreground">{item.percentage}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div className={`${colors[index % colors.length]} h-2 rounded-full`} style={{ width: `${item.percentage}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No data available</p>
              )}
            </div>

            {/* Team Performance */}
            {analyticsData?.surveyor_performance?.length > 0 ? (
              <TeamPerformanceCard surveyors={analyticsData.surveyor_performance} />
            ) : (
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Team Performance</h3>
                <p className="text-sm text-muted-foreground">No data available</p>
              </div>
            )}

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">System Status</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Database</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-sm font-medium text-success">Online</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">API Services</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-sm font-medium text-success">Operational</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Backup Status</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-sm font-medium text-success">Up to date</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Last Sync</span>
                  <span className="text-sm font-medium text-foreground">2 min ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;